import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useObserver } from 'mobx-react-lite';
import { useTheme } from '@material-ui/core/styles';

import { mediaQueries } from 'shared/styles';

import useUser from 'src/hooks/use-user';
import useUI from 'src/hooks/use-ui';
import useViewport from 'src/hooks/use-viewport';
import { useIsMobile } from 'src/hooks/use-is-mobile';
import { useIntersectionObserver } from 'src/hooks/use-intersection-observer';
import { PBB_NAME } from 'src/payments/constants';
import { BannerSlideWrapper } from 'src/components/menu-image-banner';
import type { MenuImageBannerSlide } from 'src/components/menu-image-banner';
import type { ValidIncentiveValue } from 'src/payments/hooks/shared/incentive-types';
import { useIncentives, UserType } from 'src/payments/hooks/use-incentives/use-incentives';
import { useCashBackSurfaceGate } from 'src/payments/hooks/use-cash-back-surface-gate';
import { useIncentiveExposure } from 'src/payments/hooks/use-incentive-exposure';
import { useOpenDutchiePay } from 'src/payments/hooks/use-open-dutchie-pay/use-open-dutchie-pay';
import { Typography } from 'src/typography';

export const TEST_ID_CASH_BACK_HOME_BANNER = 'cash-back-home-banner';

const ARTWORK_DESKTOP_SRC = '/images/pay-by-bank-cash-back-home-artwork-desktop.png';
const ARTWORK_MOBILE_SRC = '/images/pay-by-bank-cash-back-home-artwork-mobile.png';
const EXPOSURE_VISIBILITY_THRESHOLD = 0.5;

type CashBackHomeBannerContentProps = {
  cashBackValue: ValidIncentiveValue;
  userType: UserType;
  isCashBack: boolean;
  onLinkBank: () => void;
};

const CashBackHomeBannerContent = ({
  cashBackValue,
  userType,
  isCashBack,
  onLinkBank,
}: CashBackHomeBannerContentProps): JSX.Element => {
  const UI = useUI();
  const viewport = useViewport();
  const theme = useTheme();
  const bannerRef = useRef<HTMLElement>(null);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const visibilityEntry = useIntersectionObserver(bannerRef, {
    threshold: EXPOSURE_VISIBILITY_THRESHOLD,
    freezeOnceVisible: hasBeenVisible,
  });
  const isIframe = useObserver(() => UI.isIframe);
  const isMobileViewport = useIsMobile();
  // Embedded menus report the host viewport separately; match MenuImage's artwork selection.
  const isMobile = isIframe && viewport.width > 0 ? viewport.width < theme.breakpoints.values.sm : isMobileViewport;
  const isActionable = userType === UserType.UNENROLLED;

  useEffect(() => {
    if (
      !hasBeenVisible &&
      visibilityEntry?.isIntersecting &&
      visibilityEntry.intersectionRatio >= EXPOSURE_VISIBILITY_THRESHOLD
    ) {
      setHasBeenVisible(true);
    }
  }, [hasBeenVisible, visibilityEntry?.intersectionRatio, visibilityEntry?.isIntersecting]);

  useIncentiveExposure({
    incentiveValue: hasBeenVisible ? cashBackValue : undefined,
    userType,
    isCashBack,
    source: 'home_banner',
  });

  return (
    <BannerSlideWrapper
      onClick={isActionable ? onLinkBank : undefined}
      ariaLabel={isActionable ? `Link your bank to claim ${PBB_NAME} cash back` : undefined}
    >
      <Banner
        ref={bannerRef}
        aria-label={`${PBB_NAME} cash back promotion`}
        data-testid={TEST_ID_CASH_BACK_HOME_BANNER}
      >
        <TextContent>
          <Headline tag='h2' size='xlarge'>
            Get ${cashBackValue} back on your first purchase with {PBB_NAME}!
          </Headline>
          <Body size='xlarge' tag='p'>
            Link your bank, and get ${cashBackValue} cash back the first time you pay for an order with your bank.
          </Body>
          {isActionable && <LinkBankLabel>Link your bank now</LinkBankLabel>}
          <Disclaimer size='large' tag='p'>
            *Only valid the first time you use {PBB_NAME} to pay for your order.
          </Disclaimer>
        </TextContent>
        <Artwork aria-hidden='true'>
          <ArtworkImage src={isMobile ? ARTWORK_MOBILE_SRC : ARTWORK_DESKTOP_SRC} alt='' />
        </Artwork>
      </Banner>
    </BannerSlideWrapper>
  );
};

export const useCashBackHomeBannerSlide = (): MenuImageBannerSlide | null => {
  const UI = useUI();
  const user = useUser();
  const isLoggedIn = useObserver(() => user.isLoggedIn);
  const { showCashBack, cashBackValue } = useCashBackSurfaceGate('homeBanner');
  const { userType, isCashBack } = useIncentives();
  const { openDutchiePay } = useOpenDutchiePay({ enrollmentSource: `home-page-${String(UI?.variant)}` });

  const surfaceSuppressed = UI.isKiosk || UI.isMobileEcommApp;
  const showBanner = showCashBack && cashBackValue !== undefined && isLoggedIn && !surfaceSuppressed;

  if (!showBanner || cashBackValue === undefined) {
    return null;
  }

  return {
    id: TEST_ID_CASH_BACK_HOME_BANNER,
    content: (
      <CashBackHomeBannerContent
        cashBackValue={cashBackValue}
        userType={userType}
        isCashBack={isCashBack}
        onLinkBank={openDutchiePay}
      />
    ),
  };
};

export const CashBackHomeBanner = (): JSX.Element | null => {
  const slide = useCashBackHomeBannerSlide();

  return slide ? <>{slide.content}</> : null;
};

const Banner = styled.section`
  position: relative;
  display: flex;
  align-items: center;
  overflow: hidden;
  width: 100%;
  container-type: inline-size;
  aspect-ratio: 3 / 1;
  border-radius: var(--dcm-cash-back-home-banner-radius, var(--dct--customized-radius-lg, 12px));
  background-color: var(--dcm-cash-back-home-banner-background, ${({ theme }) => theme.colors.green20});

  @media ${mediaQueries.largePhone} {
    aspect-ratio: 2.22 / 1;
  }
`;

const TextContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: clamp(6px, 1.333cqw, 16px);
  max-width: 55%;
  padding: clamp(12px, 3.333cqw, 40px) clamp(12px, 2.667cqw, 32px);
  color: var(--dcm-cash-back-home-banner-text-color, ${({ theme }) => theme.colors.green90});
`;

const Headline = styled(Typography.Heading)`
  margin: 0;
  font-size: var(--dcm-cash-back-home-banner-headline-size, clamp(16px, 4cqw, 48px));
`;

const Body = styled(Typography.Body)`
  display: none;
  font-size: clamp(12px, 1.5cqw, 18px);
  line-height: 1.3;

  ${({ theme }) => theme.breakpoints.up('md')} {
    display: block;
  }
`;

const Disclaimer = styled(Typography.Label)`
  font-size: clamp(10px, 1.167cqw, 14px);
  line-height: 1.2;
`;

const LinkBankLabel = styled.span`
  padding: clamp(6px, 0.667cqw, 8px) clamp(12px, 1.667cqw, 20px);
  border-radius: var(--dcm-cash-back-home-banner-button-radius, 999px);
  background-color: var(--dcm-cash-back-home-banner-button-background, ${({ theme }) => theme.colors.green90});
  color: var(--dcm-cash-back-home-banner-button-color, ${({ theme }) => theme.colors.white});
  font-size: var(--dcm-cash-back-home-banner-button-font-size, clamp(12px, 1.333cqw, 16px));
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
`;

const Artwork = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: auto;
  height: 100%;
  display: block;
`;

const ArtworkImage = styled.img`
  width: auto;
  height: 100%;
  display: block;
`;
